import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  ThemeProvider,
  createTheme,
  Autocomplete,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import api from "../../../services/api";

// Supabase theme for modal
const supabaseTheme = createTheme({
  palette: {
    primary: {
      main: "hsl(210, 60%, 53%)",
      light: "hsl(210, 60%, 63%)",
      dark: "hsl(210, 60%, 43%)",
      contrastText: "#ffffff",
    },
    background: {
      default: "hsl(0, 0%, 100%)",
      paper: "hsl(0, 0%, 100%)",
    },
    text: {
      primary: "hsl(0, 0%, 13%)",
      secondary: "hsl(0, 0%, 40%)",
    },
    divider: "hsl(0, 0%, 90%)",
  },
  typography: {
    fontFamily:
      "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "4px 8px",
            fontSize: "13px",
          },
        },
        option: {
          fontSize: "13px",
          padding: "8px 12px",
          fontFamily: "'Outfit', sans-serif",
        },
        listbox: {
          padding: "4px 0",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            fontFamily: "'Outfit', sans-serif",
            fontSize: "13px",
            borderRadius: "6px",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "hsl(210, 60%, 53%)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "hsl(210, 60%, 53%)",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            fontFamily: "'Outfit', sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            "&.Mui-focused": {
              color: "hsl(210, 60%, 53%)",
            },
          },
          "& .MuiInputBase-input": {
            padding: "10px 12px",
            fontSize: "13px",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "8px 20px",
          fontSize: "13px",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "12px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      },
    },
  },
});

const SigningAuthorityModal = ({ open, onClose, onSubmit, mouId }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch available signers when modal opens
  useEffect(() => {
    if (open && mouId) {
      fetchAvailableSigners();
    }
  }, [open, mouId]);

  const fetchAvailableSigners = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      const response = await api.get(`/accounts/available-signers/${mouId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const { available_people } = response.data;
      setUsers(available_people || []);
    } catch (error) {
      console.error("Error fetching available signers:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (event, newValue) => {
    setSelectedUser(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) return;

    try {
      setSubmitting(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      // Create FormData object
      const formData = new FormData();
      formData.append("mou_id", mouId);
      formData.append("selected_person_id", selectedUser.id);

      const response = await api.post("/accounts/delegate-signing", formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Delegation successful:", response.data);

      alert("Signing authority sent successfully.");

      // Call the onSubmit callback with the selected user data
      onSubmit(selectedUser);

      // Reset the selected user
      setSelectedUser(null);
    } catch (error) {
      alert( error?.response?.data?.detail || "Failed to send signing authority. Please try again.");
      console.error("Error delegating signing authority:", error);
      // You can add error handling here (e.g., show error message to user)
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    onClose();
  };

  return (
    <ThemeProvider theme={supabaseTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: "hsl(0, 0%, 100%)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 20px 12px 20px",
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: "17px",
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "-0.02em",
          }}
        >
          Signing Authority
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            sx={{
              color: "text.secondary",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{
              padding: "12px 20px",
            }}
          >
            {loading ? (
              // Beautiful Skeleton Loading State
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Description Skeleton */}
                <Box>
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={16}
                    sx={{
                      borderRadius: "4px",
                      backgroundColor: "rgba(0, 0, 0, 0.06)",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={16}
                    sx={{
                      borderRadius: "4px",
                      backgroundColor: "rgba(0, 0, 0, 0.06)",
                      mt: 0.5,
                    }}
                  />
                </Box>

                {/* Autocomplete Field Skeleton */}
                <Skeleton
                  variant="rounded"
                  height={46}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.06)",
                  }}
                />

                {/* Details Card Skeleton */}
                <Box
                  sx={{
                    padding: "12px",
                    backgroundColor: "hsl(210, 60%, 98%)",
                    borderRadius: "6px",
                    border: "1px solid hsl(210, 60%, 90%)",
                  }}
                >
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    sx={{
                      borderRadius: "4px",
                      backgroundColor: "rgba(66, 133, 244, 0.15)",
                      mb: 1,
                    }}
                  />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}
                  >
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={14}
                      sx={{
                        borderRadius: "4px",
                        backgroundColor: "rgba(66, 133, 244, 0.15)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="95%"
                      height={14}
                      sx={{
                        borderRadius: "4px",
                        backgroundColor: "rgba(66, 133, 244, 0.15)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="75%"
                      height={14}
                      sx={{
                        borderRadius: "4px",
                        backgroundColor: "rgba(66, 133, 244, 0.15)",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              // Actual Form Content
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    marginBottom: 2,
                    fontSize: "12px",
                    lineHeight: 1.5,
                  }}
                >
                  Search and select your email to confirm your details
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Autocomplete
                    value={selectedUser}
                    onChange={handleUserSelect}
                    options={users}
                    disabled={submitting}
                    getOptionLabel={(option) => option.email}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {option.email}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "text.secondary",
                            }}
                          >
                            {option.name}
                            {option.designation && ` · ${option.designation}`}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Email"
                        placeholder="Search by email..."
                        required
                        size="small"
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        fontSize: "13px",
                      },
                    }}
                  />

                  {selectedUser && (
                    <Box
                      sx={{
                        padding: "12px",
                        backgroundColor: "hsl(210, 60%, 98%)",
                        borderRadius: "6px",
                        border: "1px solid hsl(210, 60%, 90%)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "text.secondary",
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Selected Details
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "text.secondary",
                              minWidth: "70px",
                            }}
                          >
                            Name:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "text.primary",
                            }}
                          >
                            {selectedUser.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "text.secondary",
                              minWidth: "70px",
                            }}
                          >
                            Email:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "text.primary",
                            }}
                          >
                            {selectedUser.email}
                          </Typography>
                        </Box>
                        {selectedUser.designation && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "text.secondary",
                                minWidth: "70px",
                              }}
                            >
                              Designation:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "text.primary",
                              }}
                            >
                              {selectedUser.designation}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              padding: "12px 20px 20px 20px",
              gap: 1.5,
            }}
          >
            {loading ? (
              // Button Skeletons
              <>
                <Skeleton
                  variant="rounded"
                  width={85}
                  height={36}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.06)",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={85}
                  height={36}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: "rgba(66, 133, 244, 0.15)",
                  }}
                />
              </>
            ) : (
              // Actual Buttons
              <>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  disabled={submitting}
                  sx={{
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!selectedUser || submitting}
                  sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    minWidth: "85px",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      transform: "translateY(-1px)",
                      transition: "all 0.2s ease",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                      color: "rgba(0, 0, 0, 0.26)",
                    },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
};

export default SigningAuthorityModal;
